<template>
  <DxScrollView class="opportunity-details-container">
    <div v-if="opportunity">
      <!-- Back Button -->
      <div class="back-button-section">
        <DxButton icon="arrowleft" text="Înapoi la listă" styling-mode="text" @click="$emit('back')" class="back-btn" />
      </div>

      <!-- Header Section -->
      <div class="opportunity-header">
        <div class="header-top">
          <div class="header-left">
            <div class="opportunity-icon">
              <i class="dx-icon-box"></i>
            </div>
            <div class="header-info">
              <div class="opportunity-title-container"
                :class="{ 'is-editable': canEditOpportunity && !isCurrentStatusComplete }">
                <h1 v-if="!isEditingTitle" class="opportunity-title" @click="editTitle">
                  {{ opportunity.Titlu || 'Oportunitate fără titlu' }}
                  <i v-if="canEditOpportunity && !isCurrentStatusComplete" class="dx-icon-edit edit-hint-icon"></i>
                </h1>
                <DxTextBox v-else ref="titleInput" class="opportunity-title-input" :value="opportunity.Titlu"
                  placeholder="Introdu un titlu..." @focus-out="saveTitle($event)" @enter-key="saveTitle($event)" />
              </div>
              <div class="opportunity-meta">
                <span
                  class="meta-item meta-item--contact"
                  :class="{ 'meta-item--clickable': opportunity.ContactID }"
                  :title="opportunity.ContactID ? 'Click pentru detalii contact' : 'Persoană de contact'"
                  @click="openContact">
                  <i class="dx-icon-card"></i>
                  {{
                    opportunity.Contact
                      ? `${opportunity.Contact.Nume ?? ''} ${opportunity.Contact.Prenume ?? ''}`.trim()
                      : 'Fără contact'
                  }}
                  <i v-if="opportunity.ContactID" class="dx-icon-search meta-item--link-icon"></i>
                </span>

                <span
                  class="meta-item meta-item--account"
                  :class="{ 'meta-item--clickable': opportunity.AccountID }"
                  :title="opportunity.AccountID ? 'Click pentru detalii partener' : 'Cont / Partener'"
                  @click="openPartener">
                  <i class="dx-icon-home"></i>
                  {{ opportunity.Account?.Nume || opportunity.AccountNume || 'Fără partener' }}
                  <i v-if="opportunity.AccountID" class="dx-icon-search meta-item--link-icon"></i>
                </span>

                <span class="meta-item meta-item--owner" title="Responsabil (Owner)">
                  <i class="dx-icon-user"></i>
                  {{ opportunity.Owner || 'Fără agent' }}
                </span>

                <span class="meta-item meta-item--date" title="Data creării">
                  <i class="dx-icon-event"></i>
                  {{ formatDate(opportunity.Data) }}
                </span>

                <span class="meta-item meta-item--source" v-if="opportunity.Sursa?.Nume" title="Sursă Lead">
                  <i class="dx-icon-link"></i>
                  {{ opportunity.Sursa.Nume }}
                </span>

              </div>
            </div>
          </div>
          <div class="header-actions">
            <DxButton v-if="isCurrentStatusComplete" icon="refresh" text="Redeschide Oportunitate"
              styling-mode="outlined" type="default" class="reopen-btn" @click="confirmReopen"
              :disabled="isUpdatingStatus || !canEditOpportunity" />
          </div>
        </div>

        <!-- Salesforce-style Status Path -->
        <div class="status-path-container">
          <div class="status-path">
            <div v-for="(step, index) in statusSteps" :key="index"
              :class="['status-step', getStepClass(step, index), { 'clickable': canChangeToStatus(step), 'updating': isUpdatingStatus }]"
              @click="handleStepClick(step)" :title="getStepTooltip(step)">
              <span class="step-label">{{ step.Nume }}</span>
              <div class="step-arrow"></div>
            </div>
          </div>
        </div>

        <!-- Status Description Banner -->
        <div :class="['status-description-banner', statusBannerClass]"
          v-if="isCurrentStatusComplete && opportunity.DescriereStatus">
          <div class="banner-icon">
            <i class="dx-icon-info"></i>
          </div>
          <div class="banner-content">
            <div class="banner-label">Motiv / Descriere Finalizare:</div>
            <div class="banner-text">{{ opportunity.DescriereStatus }}</div>
          </div>
        </div>
      </div>

      <!-- Finalize Opportunity Choice Popup -->
      <DxPopup v-model:visible="isFinalizePopupVisible" :drag-enabled="false" :close-on-outside-click="true"
        :show-title="true" :width="500" height="auto" title="Finalizare Oportunitate" :show-close-button="true">
        <div class="finalize-choice-content">
          <p class="finalize-message">Cum doriți să finalizați această oportunitate?</p>

          <div class="finalize-description-field">
            <label class="field-label">
              <i class="dx-icon-edit" style="margin-right: 4px; vertical-align: middle; color: #3b82f6;"></i>
              Descriere / Motiv: <span style="color: #ef4444;">*</span>
            </label>
            <DxTextArea v-model:value="finalizeDescription" :height="80"
              placeholder="Introduceți un motiv sau o descriere pentru această acțiune (obligatoriu)..." />
          </div>

          <div class="finalize-options">
            <div class="finalize-option won" @click="selectTerminalStatus('won')">
              <div class="option-icon">
                <i class="dx-icon-check"></i>
              </div>
              <div class="option-label">Câștigată</div>
            </div>
            <div class="finalize-option lost" @click="selectTerminalStatus('lost')">
              <div class="option-icon">
                <i class="dx-icon-close"></i>
              </div>
              <div class="option-label">Pierdută</div>
            </div>
          </div>


        </div>
      </DxPopup>

      <!-- Status Change Confirmation Popup -->
      <DxPopup v-model:visible="showStatusConfirmation" :drag-enabled="false" :close-on-outside-click="true"
        :show-title="true" :width="450" height="auto" title="Confirmare Schimbare Status">
        <div class="status-confirmation-content">
          <div class="confirmation-icon">
            <i :class="statusChangeIcon" :style="{ color: statusChangeColor }"></i>
          </div>
          <p class="confirmation-message">{{ statusChangeMessage }}</p>
          <div class="status-change-info">
            <div class="status-from">
              <span class="label">Status curent:</span>
              <span class="value">{{ opportunity?.Status?.Nume || opportunity?.Status || '-' }}</span>
            </div>
            <div class="status-arrow">
              <i class="dx-icon-arrowright"></i>
            </div>
            <div class="status-to">
              <span class="label">Status nou:</span>
              <span class="value">{{ pendingStatus?.Nume }}</span>
            </div>
          </div>

        </div>
        <DxToolbarItem widget="dxButton" toolbar="bottom" location="after" :options="confirmButtonOptions" />
        <DxToolbarItem widget="dxButton" toolbar="bottom" location="after" :options="cancelButtonOptions" />
      </DxPopup>

      <!-- Key Metrics Cards -->
      <div class="metrics-section">
        <div class="metric-card">
          <div class="metric-icon" style="background: #dbeafe;">
            <i class="dx-icon-money" style="color: #1e40af;"></i>
          </div>
          <div class="metric-content">
            <div class="metric-label">Buget Estimat</div>
            <div class="metric-value">{{ formatCurrency(opportunity.BugetEstimat, opportunity.Valuta?.Simbol) }}</div>
          </div>
        </div>
        <!-- 
        <div class="metric-card">
          <div class="metric-icon" style="background: #d1fae5;">
            <i class="dx-icon-money" style="color: #065f46;"></i>
          </div>
          <div class="metric-content">
            <div class="metric-label">Venit Estimat</div>
            <div class="metric-value">{{ formatCurrency(opportunity.VenitEstimat, opportunity.Valuta?.Simbol) }}</div>
          </div>
        </div> -->

        <div class="metric-card">
          <div class="metric-icon" style="background: #fef3c7;">
            <i class="dx-icon-percent" style="color: #78350f;"></i>
          </div>
          <div class="metric-content">
            <div class="metric-label">Probabilitate Status</div>
            <div class="metric-value-with-bar">
              <span class="metric-value">{{ opportunity.Probabilitate || 0 }}%</span>
              <div class="probability-bar-small">
                <div class="probability-fill-small"
                  :style="{ width: (opportunity.Probabilitate || 0) + '%', backgroundColor: getProbabilityColor(opportunity.Probabilitate || 0) }">
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="metric-card">
          <div class="metric-icon" style="background: #e0e7ff;">
            <i class="dx-icon-chart" style="color: #3730a3;"></i>
          </div>
          <div class="metric-content">
            <div class="metric-label">Șansa de Câștig</div>
            <div class="metric-value-with-bar">
              <span class="metric-value">{{ opportunity.ProbabilitateCastig || 0 }}%</span>
              <div class="probability-bar-small">
                <div class="probability-fill-small"
                  :style="{ width: (opportunity.ProbabilitateCastig || 0) + '%', backgroundColor: getProbabilityColor(opportunity.ProbabilitateCastig || 0) }">
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="metric-card">
          <div class="metric-icon" style="background: #fecaca;">
            <i class="dx-icon-event" style="color: #991b1b;"></i>
          </div>
          <div class="metric-content">
            <div class="metric-label">Închidere Estimată</div>
            <div class="metric-value">{{ formatDate(opportunity.DataInchidereEstimat) }}</div>
          </div>
        </div>
      </div>

      <!-- Two Column Layout -->
      <div class="two-column-layout">
        <!-- Left Column - Details Form -->
        <div class="left-column">
          <div class="details-card">
            <div class="card-header">
              <div class="card-header-icon">
                <i class="dx-icon-doc"></i>
              </div>
              <h3 class="card-header-title">Detalii Oportunitate</h3>
            </div>
            <div class="card-body">
              <OpportunityForm :opportunity-data="opportunity" :is-editing-prop="false"
                :is-locked="isCurrentStatusComplete" :allow-edit="canEditOpportunity" :campanii="listaCampanii"
                @contact-changed="handleContactChange" />
            </div>
          </div>
        </div>

        <!-- Right Column - Activities & Products -->
        <div class="right-column">
          <!-- Activities Section -->
          <div class="section-card">
            <div class="card-header">
              <div class="card-header-icon" style="background: linear-gradient(135deg, #10b981 0%, #34d399 100%);">
                <i class="dx-icon-event"></i>
              </div>
              <h3 class="card-header-title">Activități & Întâlniri</h3>
            </div>
            <div class="card-body">
              <CardActivity :opportunity-id="opportunity?.ID" :opportunity-data="opportunity"
                :account-id="opportunity?.AccountID" :is-locked="isCurrentStatusComplete || !canEditOpportunity" />
            </div>
          </div>

          <!-- Contacts Section -->
          <div class="section-card">
            <div class="card-header">
              <div class="card-header-icon" style="background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);">
                <i class="dx-icon-group"></i>
              </div>
              <h3 class="card-header-title">Contacte</h3>
            </div>
            <div class="card-body">
              <CardContacts :partener-id="opportunity?.AccountID" :highlight-contact-id="opportunity?.ContactID"
                :allow-contact-selection="!isCurrentStatusComplete && canEditOpportunity"
                @contact-selected="handleContactChange" />
            </div>
          </div>

          <!-- Products Section -->
          <!-- <div class="section-card">
            <div class="card-header">
              <div class="card-header-icon" style="background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);">
                <i class="dx-icon-box"></i>
              </div>
              <h3 class="card-header-title">Produse</h3>
            </div>
            <div class="card-body">
              <CardProducts :opportunity-id="opportunity?.ID" :opportunity-data="opportunity" />
            </div>
          </div> -->

          <div class="section-card">
            <div class="card-header">
              <div class="card-header-icon" style="background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);">
                <i class="dx-icon-box"></i>
              </div>
              <h3 class="card-header-title">Oferte</h3>
            </div>
            <div class="card-body">
              <CardOferte :opportunity-id="opportunity?.ID" :opportunity-data="opportunity"
                @show-products="onShowProducts" :is-locked="isCurrentStatusComplete || !canEditOpportunity"
                :is-ofertare-status="isOfertareStatus" />
            </div>
          </div>
        </div>
      </div>

      <!-- Products Popup -->
      <DxPopup v-model:visible="isProductsPopupVisible" :drag-enabled="true" :close-on-outside-click="false"
        :show-title="true" :width="1400" :height="600" :hide-on-outside-click="false" :show-close-button="true">
        <template #title>
          <div class="popup-header"
            style="margin: -16px -20px; padding: 16px 24px; background: linear-gradient(135deg, #ff2c21 0%, #ff5447 100%); display: flex; align-items: center; gap: 16px;">
            <span class="dx-icon-box"
              style="font-size: 24px; width: 45px; height: 45px; display: flex; align-items: center; justify-content: center; border-radius: 8px; color: white; background: rgba(255,255,255,0.2);"></span>
            <div style="display: flex; flex-direction: column; color: white; flex: 1;">
              <span style="font-size: 16px; font-weight: 700;">Produse Ofertă: {{ selectedOffer?.Nume }}</span>
              <span style="font-size: 12px; opacity: 0.9;">Vizualizează și gestionează produsele din această
                ofertă</span>
            </div>
            <DxButton icon="close" styling-mode="text" @click="isProductsPopupVisible = false"
              :element-attr="{ style: 'color: white !important; background: rgba(255,255,255,0.1); border-radius: 8px;' }" />
          </div>
        </template>
        <div class="popup-body-modern" style="padding: 0; height: 100%;">
          <CardProducts v-if="isProductsPopupVisible && selectedOffer?.ID" :opportunity-id="opportunity?.ID"
            :opportunity-data="opportunity" :offer-id="selectedOffer?.ID" :auto-add="isAutoAddTriggered"
            :is-locked="isCurrentStatusComplete || !canEditOpportunity" />
        </div>
      </DxPopup>

      <!-- Contact Details Popup -->
      <DxPopup v-model:visible="isContactPopupVisible" :drag-enabled="false" :close-on-outside-click="true"
        :show-title="false" :full-screen="true" :show-close-button="false">
        <div style="height: 100%; overflow: hidden; position: relative;">
          <DxButton icon="close" styling-mode="text" class="popup-close-btn" @click="isContactPopupVisible = false" />
          <div v-if="isLoadingContact" class="popup-loading">
            <i class="dx-icon-refresh popup-loading-icon"></i>
            <span>Se încarcă...</span>
          </div>
          <GridContactDetalii v-else-if="selectedContactData" :contact="selectedContactData" @back="isContactPopupVisible = false" />
        </div>
      </DxPopup>

      <!-- Partener Details Popup -->
      <DxPopup v-model:visible="isPartenerPopupVisible" :drag-enabled="false" :close-on-outside-click="true"
        :show-title="false" :full-screen="true" :show-close-button="false">
        <div style="height: 100%; overflow: hidden; position: relative;">
          <DxButton icon="close" styling-mode="text" class="popup-close-btn" @click="isPartenerPopupVisible = false" />
          <div v-if="isLoadingPartener" class="popup-loading">
            <i class="dx-icon-refresh popup-loading-icon"></i>
            <span>Se încarcă...</span>
          </div>
          <GridParteneriDetalii v-else-if="selectedPartenerData" :partener="selectedPartenerData" @back="isPartenerPopupVisible = false" />
        </div>
      </DxPopup>
    </div>

    <div v-else class="no-opportunity">
      <div class="no-opportunity-icon">
        <i class="dx-icon-box"></i>
      </div>
      <p class="no-opportunity-text">Nicio oportunitate selectată</p>
      <p class="no-opportunity-subtext">Selectează o oportunitate din listă pentru a vedea detaliile</p>
    </div>
  </DxScrollView>
</template>

<script lang="ts">

import { defineComponent } from 'vue';
import { DxScrollView } from 'devextreme-vue/scroll-view';
import { DxButton } from 'devextreme-vue/button';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import { DxTextArea } from 'devextreme-vue/text-area';
import { DxTextBox } from 'devextreme-vue/text-box';
import OpportunityForm from '@/components/library/opportunity-form.vue';
import CardActivity from '@/components/library/card-activity.vue';
import CardProducts from '@/components/library/card-products.vue';
import ODataStore from 'devextreme/data/odata/store';
import DataSource from 'devextreme/data/data_source';
import AppSetting from '../../../settings/appSettings';
import CrmSales from '@/services/Comercial/CrmSales';
import CrmContacts from '@/services/Comercial/CrmContacts';
import notify from 'devextreme/ui/notify'
import CardOferte from '@/components/library/card-oferte.vue';
import CardContacts from '@/components/library/card-contacts.vue';
import GridContactDetalii from './GridContactDetalii.vue';
import GridParteneriDetalii from './GridParteneriDetalii.vue';
import CrmParteneri from '@/services/Comercial/CrmParteneri';

// Tab-urile de oportunități remontează complet acest component la fiecare comutare
// (v-if în GridOportunitati.vue), deci mounted() rulează din nou de fiecare dată.
// Cache la nivel de modul ca să nu reașteptăm un nou round-trip de rețea la fiecare
// schimbare de tab — altfel canEditOpportunity rămâne fals (buton Adaugă Ofertă
// dezactivat) până se rezolvă noul apel.
let cachedPermissionsPromise: Promise<any> | null = null;
function fetchPermissionsCached() {
  if (!cachedPermissionsPromise) {
    cachedPermissionsPromise = CrmSales.getPermissions().catch((err) => {
      cachedPermissionsPromise = null;
      throw err;
    });
  }
  return cachedPermissionsPromise;
}

export default defineComponent({
  components: {
    DxScrollView,
    DxButton,
    DxPopup,
    DxToolbarItem,
    DxTextArea,
    DxTextBox,
    OpportunityForm,
    CardActivity,
    CardProducts,
    CardOferte,
    CardContacts,
    GridContactDetalii,
    GridParteneriDetalii
  },
  props: {
    opportunity: Object,
  },
  data() {
    return {
      listaCampanii: [],
      statusSteps: [], // Initialized as empty, populated via fetchStatuses
      showStatusConfirmation: false,
      pendingStatus: null,
      isUpdatingStatus: false,
      selectedOffer: null as any,
      isProductsPopupVisible: false,
      isAutoAddTriggered: false,
      isFinalizePopupVisible: false,
      finalizeDescription: '',
      userRights: [] as any[],
      loggedUserAgentId: null as string | null,
      canEditOpportunity: false,
      isEditingTitle: false,
      isContactPopupVisible: false,
      selectedContactData: null as any,
      isLoadingContact: false,
      isPartenerPopupVisible: false,
      selectedPartenerData: null as any,
      isLoadingPartener: false,
    }
  },
  computed: {
    currentStatusIndex(): number {
      const currentName = this.opportunity?.Status?.Nume || this.opportunity?.Status;
      if (!currentName) return -1;

      // Handle "Închis" as a special case or map it to the end if appropriate
      if (currentName === 'Închis' || currentName === 'Inchis') return this.statusSteps.length;

      return this.statusSteps.findIndex(step => step.Nume === currentName);
    },
    isCurrentStatusComplete(): boolean {
      const currentId = this.opportunity?.StatusID || this.opportunity?.Status?.ID;
      const currentName = this.opportunity?.Status?.Nume || this.opportunity?.Status;

      const terminalIds = [
        '74CC9424-8083-4D45-8EA6-48C1571CE4FB'.toLowerCase(), // Închis
        '0B2BCD0D-1131-46CC-A4B2-AC685FCCB80E'.toLowerCase()  // Câștigat
      ];

      if (currentId && typeof currentId === 'string' && terminalIds.includes(currentId.toLowerCase())) return true;

      return currentName === 'Câștigat' || currentName === 'Închis' || currentName === 'Castigat' || currentName === 'Inchis';
    },
    isOfertareStatus(): boolean {
      const currentId = this.opportunity?.StatusID || this.opportunity?.Status?.ID;
      const currentName = this.opportunity?.Status?.Nume || this.opportunity?.Status;

      // Preferăm rezolvarea prin StatusID din statusSteps (nomenclator încărcat din BD) —
      // robust chiar dacă navigarea Status nu e expandată complet pe fluxul de încărcare curent.
      if (currentId) {
        const step = this.statusSteps.find(s => s.ID && String(s.ID).toLowerCase() === String(currentId).toLowerCase());
        if (step?.Nume) {
          return step.Nume.toString().trim().toLowerCase() === 'ofertare';
        }
      }

      return (currentName || '').toString().trim().toLowerCase() === 'ofertare';
    },
    statusChangeIcon(): string {
      const currentIndex = this.currentStatusIndex;
      const pendingIndex = this.statusSteps.indexOf(this.pendingStatus);

      if (pendingIndex > currentIndex) {
        return 'dx-icon-chevronnext'; // Avansare
      } else if (pendingIndex < currentIndex) {
        return 'dx-icon-chevronprev'; // Regres
      }
      return 'dx-icon-refresh';
    },
    statusChangeColor(): string {
      const currentIndex = this.currentStatusIndex;
      const pendingIndex = this.statusSteps.indexOf(this.pendingStatus);

      if (pendingIndex > currentIndex) {
        return '#10b981'; // Verde pentru avansare
      } else if (pendingIndex < currentIndex) {
        return '#f59e0b'; // Portocaliu pentru regres
      }
      return '#3b82f6';
    },
    statusChangeMessage(): string {
      const currentIndex = this.currentStatusIndex;
      const pendingIndex = this.statusSteps.indexOf(this.pendingStatus);

      if (pendingIndex > currentIndex) {
        return 'Ești sigur că vrei să avansezi statusul acestei oportunități?';
      } else if (pendingIndex < currentIndex) {
        return 'Atenție! Vrei să dai înapoi statusul? Această acțiune poate afecta raportările.';
      }
      return 'Vrei să schimbi statusul acestei oportunități?';
    },
    confirmButtonOptions(): any {
      return {
        text: 'Confirmă',
        type: 'success',
        stylingMode: 'contained',
        onClick: () => this.confirmStatusChange()
      };
    },
    cancelButtonOptions(): any {
      return {
        text: 'Anulează',
        type: 'normal',
        stylingMode: 'outlined',
        onClick: () => this.cancelStatusChange()
      };
    },
    statusBannerClass(): string {
      const currentId = this.opportunity?.StatusID || this.opportunity?.Status?.ID;
      const wonId = '0B2BCD0D-1131-46CC-A4B2-AC685FCCB80E'.toLowerCase();

      if (currentId && typeof currentId === 'string' && currentId.toLowerCase() === wonId) {
        return 'is-won';
      }

      const currentName = this.opportunity?.Status?.Nume || this.opportunity?.Status;
      if (currentName === 'Câștigat' || currentName === 'Castigat') {
        return 'is-won';
      }

      return 'is-lost';
    }
  },
  watch: {
    'opportunity.ID'() {
      this.selectedOffer = null;
      this.isProductsPopupVisible = false;
      this.evaluatePermissions();
    },
    userRights() {
      this.evaluatePermissions();
    }
  },
  mounted() {
    const campanii = new ODataStore({
      url: AppSetting.API_Url_ComercialOdata + 'CRM_CautareCampaniiMarketing',
      key: 'ID',
      version: 4,
      beforeSend: (e) => {
        e.headers = {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt"),
        };
      },
    });

    campanii.load().then(data => {
      this.listaCampanii = data;
    });

    fetchPermissionsCached().then((res) => {
      if (res && res.data) {
        this.userRights = res.data.Rights || [];
        this.loggedUserAgentId = res.data.MyAgentDefaultId;
        this.evaluatePermissions();
      }
    }).catch((err) => {
      console.error('Error fetching permissions:', err);
    });

    this.fetchStatuses();
  },
  methods: {
    fetchStatuses() {
      const statusStore = new DataSource({
        store: {
          type: 'odata',
          url: AppSetting.API_Url_ComercialOdata + 'CRM_StariOportunitati',
          key: 'ID',
          version: 4,
          beforeSend: (e: any) => {
            e.headers = {
              "Content-Type": "application/json",
              "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
            };
          }
        },
        select: [
          'ID',
          'Nume',
          'Pondere'
        ],
        sort: [{ selector: "NrOrd", desc: false }]
      });

      statusStore.load().then((data: any[]) => {
        const terminalIds = [
          '74CC9424-8083-4D45-8EA6-48C1571CE4FB'.toLowerCase(), // Închis
          '0B2BCD0D-1131-46CC-A4B2-AC685FCCB80E'.toLowerCase()  // Câștigat
        ];

        // Filter out terminal statuses from the sequential steps
        const filteredSteps = data.filter(step => {
          const id = step.ID?.toString()?.toLowerCase();
          return id && !terminalIds.includes(id);
        });

        // Add virtual "Finalize" step
        this.statusSteps = [
          ...filteredSteps,
          {
            ID: 'virtual-finalize',
            Nume: 'Finalizează oportunitate',
            isVirtualFinalize: true
          }
        ];
      }).catch(error => {
        console.error("Failed to load statuses", error);
      });
    },
    evaluatePermissions() {
      const data = this.opportunity as any;
      if (!data) {
        this.canEditOpportunity = false;
        return;
      }

      const rawOwnerId = data.OwnerID || data.OwnerId || data.ownerid || data.AgentID || data.AgentId || data.agentid;
      const ownerId = rawOwnerId ? String(rawOwnerId).toLowerCase() : null;

      if (!this.userRights || this.userRights.length === 0) {
        this.canEditOpportunity = false;
        return;
      }

      if (ownerId && this.loggedUserAgentId && ownerId === this.loggedUserAgentId.toLowerCase()) {
        this.canEditOpportunity = true;
        return;
      }

      if (!ownerId) {
        this.canEditOpportunity = false;
        return;
      }

      const right = this.userRights.find((r: any) =>
        r.EntityID && String(r.EntityID).toLowerCase() === ownerId
      );

      this.canEditOpportunity = right ? right.Modifica === true : false;
    },
    getStepClass(step: any, index: number): string {
      const currentStatusId = this.opportunity?.StatusID || this.opportunity?.Status?.ID;
      const currentStatusName = this.opportunity?.Status?.Nume || this.opportunity?.Status;

      // Check if we are in a terminal status
      if (this.isCurrentStatusComplete) {
        // If we are evaluating the virtual "Finalize" step, it's the current one
        if (step.isVirtualFinalize) return 'current';
        // All other steps are completed
        return 'completed';
      }

      // Standard index-based check
      const currentIndex = this.statusSteps.findIndex(s =>
        (s.ID && s.ID === currentStatusId) ||
        (s.Nume && s.Nume === currentStatusName)
      );

      if (index === currentIndex) return 'current';
      if (currentIndex !== -1 && index < currentIndex) return 'completed';

      // Special case for first step
      if (index === 0 && currentIndex > 0) return 'completed';

      return 'future';
    },
    handleStepClick(step: any) {
      if (!this.canChangeToStatus(step) && !step.isVirtualFinalize) {
        return;
      }

      if (step.isVirtualFinalize) {
        if (this.isCurrentStatusComplete || !this.canEditOpportunity) return;
        this.finalizeDescription = '';
        this.isFinalizePopupVisible = true;
        return;
      }

      if (!this.canChangeToStatus(step)) {
        return;
      }

      const statusId = step.ID;
      const currentStatusId = this.opportunity?.StatusID || this.opportunity?.Status?.ID;

      if (currentStatusId === statusId) {
        notify('Oportunitatea este deja la acest status', 'info', 2000);
        return;
      }

      this.pendingStatus = step; // Store the whole object
      this.showStatusConfirmation = true;
    },
    canChangeToStatus(step: any): boolean {
      if (!this.opportunity || this.isUpdatingStatus || !this.canEditOpportunity) return false;

      const statusId = step.ID;
      const currentStatusId = this.opportunity?.StatusID || this.opportunity?.Status?.ID;

      const currentName = this.opportunity?.Status?.Nume || this.opportunity?.Status;

      // Special check for Closed/Won completeness
      // If we are closed/won, we might not want to go back easily, but logic allows it if not same status?
      // The original code returned false if isCurrentStatusComplete.
      if (this.isCurrentStatusComplete) return false;

      if (currentStatusId && statusId) {
        if (currentStatusId === statusId) return false;
      } else if (currentName && step.Nume) {
        if (currentName === step.Nume) return false;
      }

      return true;
    },
    getStepTooltip(step: any): string {
      const statusId = step.ID;
      const currentStatusId = this.opportunity?.StatusID || this.opportunity?.Status?.ID;
      const statusName = step.Nume;

      if (!this.canEditOpportunity) {
        return 'Nu aveți dreptul să modificați această oportunitate';
      }

      if (currentStatusId && statusId) {
        if (currentStatusId === statusId) return 'Status curent';
      } else {
        const currentStatusName = this.opportunity?.Status?.Nume || this.opportunity?.Status;
        if (currentStatusName === statusName) return 'Status curent';
      }

      if (this.isCurrentStatusComplete) {
        return 'Oportunitatea este finalizată';
      }
      if (this.canChangeToStatus(step)) {
        return `Click pentru a schimba la: ${statusName}`;
      }
      return statusName;
    },
    async selectTerminalStatus(type: 'won' | 'lost') {
      if (!this.canEditOpportunity) return;
      if (!this.finalizeDescription || this.finalizeDescription.trim() === '') {
        notify('Vă rugăm să introduceți un motiv sau o descriere pentru finalizarea oportunității!', 'error', 3000);
        return;
      }
      this.isFinalizePopupVisible = false;

      const wonId = '0B2BCD0D-1131-46CC-A4B2-AC685FCCB80E';
      const lostId = '74CC9424-8083-4D45-8EA6-48C1571CE4FB';

      const targetId = type === 'won' ? wonId : lostId;
      const targetName = type === 'won' ? 'Câștigat' : 'Închis';

      // Set probability (Pondere) to 100 if Won, or 0 if Closed/Lost
      const probability = type === 'won' ? 100 : 0;

      this.pendingStatus = { ID: targetId, Nume: targetName, Pondere: probability };
      this.showStatusConfirmation = true;
    },
    async handleContactChange(newContactId: string) {
      if (!this.opportunity || !this.opportunity.ID) return;

      try {
        const updatePayload = { ContactID: newContactId };
        await CrmSales.update(this.opportunity.ID, updatePayload);

        this.opportunity.ContactID = newContactId;

        const contactResponse = await CrmContacts.getById(newContactId);
        if (contactResponse?.data) {
          this.opportunity.Contact = contactResponse.data;
        }

        notify('Persoana de contact a fost actualizată cu succes!', 'success', 2000);
        this.$emit('refresh-data');
      } catch (error) {
        console.error(error);
        notify('Eroare la actualizarea persoanei de contact.', 'error', 2000);
      }
    },
    confirmReopen() {
      if (!this.canEditOpportunity) return;
      // Find the "Nou" status in our loaded steps if possible
      const nouStatus = this.statusSteps.find(s => s.Nume === 'Nou') ||
        { ID: 'BE62B2A3-AEA9-4706-ADED-934622DBEFD9', Nume: 'Nou', Pondere: 0 };

      this.pendingStatus = nouStatus;
      this.showStatusConfirmation = true;
    },
    async confirmStatusChange() {
      if (!this.opportunity || !this.opportunity.ID || !this.pendingStatus || !this.canEditOpportunity) {
        return;
      }

      this.isUpdatingStatus = true;
      this.showStatusConfirmation = false;

      try {
        const updatePayload: any = {
          StatusID: this.pendingStatus.ID
        };

        if (typeof this.pendingStatus.Pondere !== 'undefined') {
          updatePayload.Probabilitate = this.pendingStatus.Pondere;
        }

        if (this.finalizeDescription) {
          updatePayload.DescriereStatus = this.finalizeDescription;
        } else if (this.pendingStatus.Nume === 'Nou') {
          updatePayload.DescriereStatus = '';
        }

        await CrmSales.update(this.opportunity.ID, updatePayload);

        // Update local mostly for display until refresh
        if (this.pendingStatus.Nume === 'Nou') {
          this.opportunity.DescriereStatus = '';
        } else if (this.finalizeDescription) {
          this.opportunity.DescriereStatus = this.finalizeDescription;
        }
        // Ideally we should refetch or update structure matching backend
        // Maintain object structure if it was an object
        if (typeof this.opportunity.Status === 'object') {
          this.opportunity.Status = { ...this.opportunity.Status, Nume: this.pendingStatus.Nume, ID: this.pendingStatus.ID };
        } else {
          this.opportunity.Status = this.pendingStatus.Nume;
        }
        this.opportunity.StatusID = this.pendingStatus.ID;
        if (typeof this.pendingStatus.Pondere !== 'undefined') {
          this.opportunity.Probabilitate = this.pendingStatus.Pondere;
        }

        notify(`Status actualizat la: ${this.pendingStatus.Nume}`, 'success', 3000);

        this.$emit('status-updated', this.opportunity);
      } catch (error) {
        console.error('Eroare la actualizarea statusului:', error);
        notify('Eroare la actualizarea statusului!', 'error', 2000);
      } finally {
        this.isUpdatingStatus = false;
        this.pendingStatus = null;
        this.finalizeDescription = '';
      }
    },
    cancelStatusChange() {
      this.showStatusConfirmation = false;
      this.pendingStatus = null;
      this.finalizeDescription = '';
    },
    async markAsComplete() {
      if (!this.opportunity || !this.opportunity.ID) {
        notify('Nu există o oportunitate selectată!', 'error', 2000);
        return;
      }

      const currentIndex = this.currentStatusIndex;
      if (currentIndex === -1 || currentIndex >= this.statusSteps.length - 1) {
        notify('Statusul este deja la final!', 'warning', 2000);
        return;
      }

      const nextStatus = this.statusSteps[currentIndex + 1];

      try {
        const updatePayload: any = {
          StatusID: nextStatus.ID
        };

        if (typeof nextStatus.Pondere !== 'undefined') {
          updatePayload.Probabilitate = nextStatus.Pondere;
        }

        await CrmSales.update(this.opportunity.ID, updatePayload);

        if (typeof this.opportunity.Status === 'object') {
          this.opportunity.Status = { ...this.opportunity.Status, Nume: nextStatus.Nume, ID: nextStatus.ID };
        } else {
          this.opportunity.Status = nextStatus.Nume;
        }
        this.opportunity.StatusID = nextStatus.ID;
        if (typeof nextStatus.Pondere !== 'undefined') {
          this.opportunity.Probabilitate = nextStatus.Pondere;
        }

        notify(`Status actualizat la: ${nextStatus.Nume}`, 'success', 2000);

        this.$emit('status-updated', this.opportunity);
      } catch (error) {
        console.error('Eroare la actualizarea statusului:', error);
        notify('Eroare la actualizarea statusului!', 'error', 2000);
      }
    },
    formatCurrency(value: number, currency: string = 'RON'): string {
      if (!value && value !== 0) return '-';
      const currencyCode = (currency && /^[A-Z]{3}$/i.test(currency)) ? currency.toUpperCase() : 'RON';

      return new Intl.NumberFormat('ro-RO', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    formatDate(dateString: string): string {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('ro-RO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    },
    getProbabilityColor(probability: number): string {
      if (probability >= 75) return '#10b981';
      if (probability >= 50) return '#3b82f6';
      if (probability >= 25) return '#f59e0b';
      return '#ef4444';
    },

    onOfferSelected(offer: any) {
      if (offer) {
        this.selectedOffer = offer;
        this.isProductsPopupVisible = true;
      } else {
        this.selectedOffer = null;
        this.isProductsPopupVisible = false;
      }
    },
    onShowProducts(offer: any, autoAdd: boolean = false) {
      if (offer) {
        this.selectedOffer = offer;
        this.isProductsPopupVisible = true;
        this.isAutoAddTriggered = autoAdd;
      }
    },
    async saveTitle(e: any) {
      const newValue = (e.component ? e.component.option('value') : e.value) as string;
      this.isEditingTitle = false;

      if (!this.opportunity || !this.opportunity.ID) return;
      if (newValue === this.opportunity.Titlu) return;

      try {
        const updatePayload = { Titlu: newValue };
        await CrmSales.update(this.opportunity.ID, updatePayload);
        this.opportunity.Titlu = newValue;
        notify('Titlul a fost actualizat!', 'success', 2000);
        this.$emit('refresh-data');
      } catch (error) {
        console.error(error);
        notify('Eroare la actualizarea titlului.', 'error', 2000);
      }
    },
    editTitle() {
      if (!this.canEditOpportunity || this.isCurrentStatusComplete) return;
      this.isEditingTitle = true;
      this.$nextTick(() => {
        const input = this.$refs.titleInput as any;
        if (input && input.instance) {
          input.instance.focus();
        }
      });
    },
    async openContact() {
      const contactId = this.opportunity?.ContactID;
      if (!contactId) return;

      this.isLoadingContact = true;
      this.selectedContactData = null;
      this.isContactPopupVisible = true;

      try {
        const ds = new DataSource({
          store: {
            type: 'odata',
            url: AppSetting.API_Url_ComercialOdata + 'CRM_Contacts',
            key: 'ID',
            keyType: 'Guid',
            version: 4,
            beforeSend: (e: any) => {
              e.headers = {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt"),
              };
              e.params.$expand = 'Account($select=Nume),Tara($select=Nume),Localitate($select=Localitate),Judet($select=Nume)';
            },
          },
          filter: ['ID', '=', contactId],
        });
        const result = await ds.load();
        this.selectedContactData = result[0] ?? null;
        if (!this.selectedContactData) {
          notify('Nu s-au putut încărca datele contactului.', 'error', 3000);
          this.isContactPopupVisible = false;
        }
      } catch (err) {
        console.error(err);
        notify('Eroare la încărcarea contactului.', 'error', 3000);
        this.isContactPopupVisible = false;
      } finally {
        this.isLoadingContact = false;
      }
    },
    async openPartener() {
      const partenerId = this.opportunity?.AccountID;
      if (!partenerId) return;

      this.isLoadingPartener = true;
      this.selectedPartenerData = null;
      this.isPartenerPopupVisible = true;

      try {
        const response = await CrmParteneri.getById(partenerId);
        this.selectedPartenerData = response.data ?? null;
        if (!this.selectedPartenerData) {
          notify('Nu s-au putut încărca datele partenerului.', 'error', 3000);
          this.isPartenerPopupVisible = false;
        }
      } catch (err) {
        console.error(err);
        notify('Eroare la încărcarea partenerului.', 'error', 3000);
        this.isPartenerPopupVisible = false;
      } finally {
        this.isLoadingPartener = false;
      }
    },
  },
});
</script>

<style scoped lang="scss">
.opportunity-details-container {
  background: #f8f9fa;
  min-height: 100vh;
}

/* Back Button */
.back-button-section {
  padding: 1rem 1.5rem 0.5rem;

  .back-btn {
    color: #6b7280;
    font-weight: 500;

    &:hover {
      color: #ff2c21;
    }
  }
}

.reopen-btn {
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    transform: translateY(-1px);
  }
}

/* Header Section */
.opportunity-header {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-bottom: 1px solid #e5e7eb;
  padding: 1.5rem 1.5rem 1rem;
  /* Reduced bottom padding */
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}

.header-left {
  display: flex;
  gap: 1rem;
  flex: 1;
  min-width: 300px;
}

.opportunity-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #ff2c21 0%, #ff5447 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(255, 44, 33, 0.2);

  i {
    font-size: 28px;
    color: white;
  }
}

.header-info {
  flex: 1;
}

.opportunity-title {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 32px;
}

.opportunity-title-container {
  margin-bottom: 0.5rem;
  position: relative;

  &.is-editable {
    .opportunity-title {
      cursor: pointer;
      border-radius: 4px;
      padding: 2px 4px;
      margin: -2px -4px;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: rgba(0, 0, 0, 0.03);

        .edit-hint-icon {
          opacity: 0.5;
        }
      }
    }
  }
}

.edit-hint-icon {
  font-size: 16px;
  color: #6b7280;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.opportunity-title-input {
  width: 100%;

  :deep(.dx-texteditor-container) {
    border: none;
    background: transparent;
    padding: 0;
  }

  :deep(.dx-texteditor-input) {
    font-size: 24px;
    font-weight: 700;
    padding: 2px 4px;
    margin: -2px -4px;
    color: #111827;
    background: white;
    border-radius: 4px;
    box-shadow: 0 0 0 2px #ff2c21;
    line-height: 1.3;
    height: auto;
  }
}

.opportunity-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  color: #6b7280;
  font-size: 14px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 4px 12px;
  border-radius: 20px;
  background: white;
  border: 1px solid #e5e7eb;
  font-weight: 500;
  transition: all 0.2s ease;

  i {
    font-size: 14px;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &--contact {
    background: #eff6ff;
    color: #1d4ed8;
    border-color: #dbeafe;

    i {
      color: #3b82f6;
    }
  }

  &--account {
    background: #f0fdf4;
    color: #15803d;
    border-color: #dcfce7;

    i {
      color: #22c55e;
    }
  }

  &--owner {
    background: #faf5ff;
    color: #7e22ce;
    border-color: #f3e8ff;

    i {
      color: #a855f7;
    }
  }

  &--date {
    background: #fefce8;
    color: #a16207;
    border-color: #fef9c3;

    i {
      color: #eab308;
    }
  }

  &--source {
    background: #fff1f2;
    color: #be123c;
    border-color: #ffe4e6;

    i {
      color: #f43f5e;
    }
  }
}

.meta-separator {
  display: none;
}

.meta-item--clickable {
  cursor: pointer;

  &:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12) !important;
    transform: translateY(-2px) !important;
  }

  .meta-item--link-icon {
    font-size: 11px;
    opacity: 0;
    transition: opacity 0.15s ease;
    margin-left: 2px;
  }

  &:hover .meta-item--link-icon {
    opacity: 0.7;
  }
}

.popup-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  width: 36px !important;
  height: 36px !important;
  background: rgba(0, 0, 0, 0.06) !important;
  border-radius: 50% !important;
  color: #374151 !important;

  &:hover {
    background: rgba(0, 0, 0, 0.12) !important;
    color: #111827 !important;
  }

  :deep(.dx-icon) {
    font-size: 16px !important;
  }
}

.popup-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 12px;
  color: #6b7280;
  font-size: 14px;
}

.popup-loading-icon {
  font-size: 32px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Status Path Styling */
.status-path-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
}

.status-path {
  display: flex;
  flex: 1;
  background: #e5e7eb;
  border-radius: 20px;
  /* Rounded pill shape for the whole bar */
  overflow: hidden;
  height: 36px;
}

.status-step {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1.5rem 0 2rem;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:first-child {
    padding-left: 1.5rem;
  }

  /* Chevron Arrow Effect */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: -12px;
    /* Adjust based on angle */
    width: 0;
    height: 0;
    border-top: 18px solid transparent;
    border-bottom: 18px solid transparent;
    border-left: 12px solid #f3f4f6;
    /* Match background */
    z-index: 2;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: -13px;
    /* Border width offset */
    width: 0;
    height: 0;
    border-top: 18px solid transparent;
    border-bottom: 18px solid transparent;
    border-left: 12px solid white;
    /* Separator color */
    z-index: 1;
  }

  /* Clickable state */
  &.clickable {
    cursor: pointer;

    &:hover:not(.current):not(.updating) {
      background: #e5e7eb;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

      &::after {
        border-left-color: #e5e7eb;
      }
    }
  }

  /* Updating state */
  &.updating {
    cursor: wait;
    opacity: 0.6;
  }

  /* Completed State */
  &.completed {
    background: #10b981;
    /* Green */
    color: white;

    &::after {
      border-left-color: #10b981;
    }

    &.clickable:hover:not(.updating) {
      background: #059669;

      &::after {
        border-left-color: #059669;
      }
    }
  }

  /* Current State */
  &.current {
    background: #3b82f6;
    /* Blue */
    color: white;
    cursor: default;

    &::after {
      border-left-color: #3b82f6;
    }
  }

  /* Future State */
  &.future {
    background: #f3f4f6;
    color: #6b7280;

    &.clickable:hover:not(.updating) {
      background: #e5e7eb;

      &::after {
        border-left-color: #e5e7eb;
      }
    }
  }

  /* Last child adjustments */
  &:last-child {
    padding-right: 1.5rem;

    &::after,
    &::before {
      display: none;
    }
  }
}

.current-status-action {
  flex-shrink: 0;
}

/* Metrics Section */
.metrics-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  padding: 0 1.5rem 1.5rem;
}

.metric-card {
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
}

.metric-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 24px;
  }
}

.metric-content {
  flex: 1;
  min-width: 0;
}

.metric-label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 0.375rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.metric-value {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}

.metric-value-with-bar {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.probability-bar-small {
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.probability-fill-small {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

/* Two Column Layout */
.two-column-layout {
  display: grid;
  grid-template-columns: 0.7fr 1fr;
  gap: 1.5rem;
  padding: 0 1.5rem 1.5rem;
  align-items: start;
}

.left-column {
  display: flex;
  flex-direction: column;
}

.right-column {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.details-card,
.section-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  background: linear-gradient(to bottom, #ffffff 0%, #fafafa 100%);
}

.card-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 18px;
    color: white;
  }
}

.card-header-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.3;
}

.card-body {
  padding: 1.5rem;
}

/* No Opportunity State */
.no-opportunity {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
}

.no-opportunity-icon {
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;

  i {
    font-size: 40px;
    color: #9ca3af;
  }
}

.no-opportunity-text {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
}

.no-opportunity-subtext {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .opportunity-header {
    padding: 1rem;
  }

  .header-content {
    flex-direction: column;
  }

  .header-left {
    width: 100%;
  }

  .status-path-container {
    flex-direction: column;
    align-items: stretch;
  }

  .status-path {
    overflow-x: auto;
    padding-bottom: 5px;
    /* Scrollbar space */
  }

  .metrics-section {
    grid-template-columns: 1fr;
    padding: 0 1rem 1rem;
  }

  .two-column-layout {
    grid-template-columns: 1fr;
    padding: 0 1rem 1rem;
  }

  .opportunity-title {
    font-size: 20px;
  }
}

/* Status Confirmation Popup */
/* Finalize Choice Popup Styling */
.finalize-choice-content {
  padding: 24px;
  text-align: center;
}

.finalize-message {
  font-size: 16px;
  color: #374151;
  margin-bottom: 24px;
  font-weight: 500;
}

.finalize-options {
  padding: 1rem;
  display: flex;
  gap: 20px;
  justify-content: center;
}

.finalize-option {
  flex: 1;
  padding: 24px;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  min-width: 200px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }

  .option-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
  }

  .option-label {
    font-weight: 700;
    font-size: 14px;
  }

  /* Won Option */
  &.won {
    &:hover {
      border-color: #10b981;
      background: #f0fdf4;
    }

    .option-icon {
      background: #d1fae5;
      color: #059669;
    }

    .option-label {
      color: #065f46;
    }
  }

  /* Lost Option */
  &.lost {
    &:hover {
      border-color: #ef4444;
      background: #fef2f2;
    }

    .option-icon {
      background: #fee2e2;
      color: #dc2626;
    }

    .option-label {
      color: #991b1b;
    }
  }
}

.status-confirmation-content {
  padding: 1.5rem;
  text-align: center;
}

.confirmation-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f3f4f6;

  i {
    font-size: 32px;
  }
}

.confirmation-message {
  font-size: 15px;
  color: #374151;
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
}

.status-change-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  margin-top: 1rem;
}

.status-from,
.status-to {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  .label {
    font-size: 11px;
    color: #6b7280;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.05em;
  }

  .value {
    font-size: 14px;
    color: #111827;
    font-weight: 700;
  }
}

.status-arrow {
  color: #9ca3af;
  font-size: 20px;
  padding: 0 0.5rem;
}

.finalize-description-field {
  margin-top: 24px;
  text-align: left;
  background: #f8fafc;
  padding: 16px;
  border-radius: 12px;
  border: 1px dashed #cbd5e1;
  transition: all 0.3s ease;

  &:focus-within {
    background: #ffffff;
    border-style: solid;
    border-color: #3b82f6;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
}

.field-label {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
}

.dx-textarea.dx-widget {
  border-radius: 8px;
  background-color: transparent;

  &.dx-state-focused {
    border-color: transparent !important;
  }
}

.status-description-banner {
  margin: 1.5rem 1.5rem 0;
  padding: 1rem 1.25rem;
  background: #f0f9ff;
  border-radius: 10px;
  border: 1px solid #bae6fd;
  display: flex;
  gap: 1rem;
  align-items: center;
  animation: fadeInDown 0.3s ease-out;

  &.is-won {
    background: #f0fdf4;
    border-color: #bbf7d0;

    .banner-icon {
      background: #d1fae5;
      color: #059669;
    }

    .banner-label {
      color: #059669;
    }

    .banner-text {
      color: #064e3b;
    }
  }

  &.is-lost {
    background: #fef2f2;
    border-color: #fecaca;

    .banner-icon {
      background: #fee2e2;
      color: #dc2626;
    }

    .banner-label {
      color: #dc2626;
    }

    .banner-text {
      color: #7f1d1d;
    }
  }

  .banner-icon {
    width: 36px;
    height: 36px;
    background: #e0f2fe;
    color: #0369a1;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 20px;
    }
  }

  .banner-content {
    flex: 1;
  }

  .banner-label {
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 700;
    color: #0369a1;
    letter-spacing: 0.05em;
    margin-bottom: 2px;
  }

  .banner-text {
    font-size: 14px;
    color: #0c4a6e;
    font-weight: 500;
    margin: 0;
  }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
